import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { portalApi, subscriptionApi } from '../services/pspApi';
import { usePspAuth } from '../context/PspAuthContext';
import { Alert } from '../components/Feedback';
import { EDUCATION_LEVEL_OPTIONS } from '../constants/options';
import { getPspStatusInfo } from '../utils/statusHelper';
import moment from 'moment';

const JobDetail = () => {
    const { friendly_url, id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, candidate } = usePspAuth();
    const base = `/processos-seletivos/${friendly_url}`;

    const [vacancy, setVacancy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Subscription status
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [applying, setApplying] = useState(false);
    const [applySuccessMessage, setApplySuccessMessage] = useState('');
    const [applyError, setApplyError] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            setLoading(true);
            setError('');
            setApplySuccessMessage('');
            setApplyError('');

            try {
                const res = await portalApi.getVacancyById(id);
                if (isMounted) {
                    if (res && res.status === 1 && res.vacancy) {
                        setVacancy(res.vacancy);
                    } else {
                        setError((res && res.message) || 'Vaga não encontrada.');
                    }
                }

                // If candidate is logged in, check subscription
                if (isAuthenticated) {
                    const checkRes = await subscriptionApi.check(id);
                    if (isMounted && checkRes && checkRes.status === 1) {
                        setIsSubscribed(checkRes.is_subscribed);
                        setSubscription(checkRes.subscription);
                    }
                }
            } catch (err) {
                console.error('Erro ao carregar vaga:', err);
                if (isMounted) setError('Erro ao carregar informações da vaga.');
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [id, isAuthenticated]);

    const handleApplyClick = () => {
        if (!isAuthenticated) {
            // Redirect to login preserving the return location
            navigate(`${base}/login`, { state: { returnTo: location.pathname } });
            return;
        }

        setShowConfirmModal(true);
    };

    const schoolingComparison = React.useMemo(() => {
        if (!vacancy || !vacancy.academic_background) {
            return { meets: true, required: null, current: candidate?.schooling || null };
        }
        const required = vacancy.academic_background;
        const current = candidate?.schooling || '';
        const requiredIndex = EDUCATION_LEVEL_OPTIONS.indexOf(required);
        const candidateIndex = current ? EDUCATION_LEVEL_OPTIONS.indexOf(current) : -1;

        if (requiredIndex === -1) {
            return { meets: true, required, current };
        }

        const meets = candidateIndex !== -1 && candidateIndex >= requiredIndex;
        return {
            meets,
            required,
            current: current || 'Não informada'
        };
    }, [vacancy, candidate]);

    const handleConfirmApply = async () => {
        // if (!schoolingComparison.meets) {
        //     setApplyError(
        //         `Sua escolaridade (${schoolingComparison.current}) não atinge o requisito mínimo exigido para esta vaga (${schoolingComparison.required}).`
        //     );
        //     setShowConfirmModal(false);
        //     return;
        // }

        setShowConfirmModal(false);
        setApplying(true);
        setApplyError('');
        setApplySuccessMessage('');

        try {
            const res = await subscriptionApi.apply(id);
            if (res && res.status === 1) {
                setIsSubscribed(true);
                setSubscription(res.subscription);
                setApplySuccessMessage(res.message || 'Candidatura realizada com sucesso!');
            } else {
                setApplyError((res && res.message) || 'Não foi possível concluir a inscrição.');
            }
        } catch (err) {
            console.error('Erro ao aplicar:', err);
            setApplyError('Erro ao submeter a candidatura. Tente novamente.');
        } finally {
            setApplying(false);
        }
    };

    if (loading) {
        return (
            <div className="psp-portal-main psp-center">
                <div className="psp-loading">
                    <i className="fas fa-spinner fa-spin fa-2x" />
                    <p>Carregando detalhes da vaga...</p>
                </div>
            </div>
        );
    }

    if (error || !vacancy) {
        return (
            <div className="psp-portal-main psp-portal-main--detail psp-fade-in">
                <button type="button" className="psp-back-btn" onClick={() => navigate(base)}>
                    <i className="fas fa-arrow-left" aria-hidden="true" />
                    Voltar para lista de vagas
                </button>
                <Alert type="error">{error || 'Vaga não encontrada.'}</Alert>
            </div>
        );
    }

    const statusInfo = getPspStatusInfo(vacancy);
    const { isOpen, isUpcoming, isClosed } = statusInfo;
    const initialDate = vacancy.subscriber_initial_date ? moment(vacancy.subscriber_initial_date) : null;
    const endDate = vacancy.subscriber_end_date ? moment(vacancy.subscriber_end_date) : null;

    let filesList = [];
    if (vacancy.files) {
        if (Array.isArray(vacancy.files)) {
            filesList = vacancy.files;
        } else if (typeof vacancy.files === 'string') {
            try { filesList = JSON.parse(vacancy.files); } catch (e) { filesList = []; }
        }
    }

    return (
        <div className="psp-portal-main psp-fade-in">
            <div className="psp-detail-container">
                {/* Topbar com Botão Voltar e Botão Imprimir */}
                <div className="psp-detail-topbar no-print">
                    <button type="button" className="psp-back-btn" onClick={() => navigate(base)}>
                        <i className="fas fa-arrow-left" aria-hidden="true" />
                        Voltar para lista de vagas
                    </button>

                    <button
                        type="button"
                        className="psp-btn-print"
                        onClick={() => window.print()}
                        title="Imprimir / Salvar detalhes completos do edital"
                    >
                        <i className="fas fa-print" aria-hidden="true" />
                        Imprimir / Salvar Edital
                    </button>
                </div>

                {applySuccessMessage && <Alert type="success">{applySuccessMessage}</Alert>}
                {applyError && <Alert type="warning">{applyError}</Alert>}

                {/* HERO CARD DA VAGA */}
                <div className="psp-job-hero-card">
                    <div className="psp-job-hero-header">
                        <div>
                            <span className="psp-job-hero-eyebrow">
                                Processo Seletivo: {vacancy.selection_process_title}
                            </span>
                            <h1 className="psp-job-hero-title">{vacancy.title}</h1>

                            <div className="psp-job-hero-tags">
                                {vacancy.role_name && (
                                    <span className="psp-hero-tag psp-hero-tag--primary">
                                        <i className="fas fa-briefcase" aria-hidden="true" />
                                        Cargo: {vacancy.role_name}
                                    </span>
                                )}
                                {vacancy.area_role_name && (
                                    <span className="psp-hero-tag">
                                        <i className="fas fa-layer-group" aria-hidden="true" />
                                        Área: {vacancy.area_role_name}
                                    </span>
                                )}
                                {vacancy.projects && vacancy.projects.length > 0 ? (
                                    vacancy.projects.map((proj) => (
                                        <span key={proj.id} className="psp-hero-tag">
                                            <i className="fas fa-map-marker-alt" aria-hidden="true" />
                                            {proj.name}
                                        </span>
                                    ))
                                ) : (
                                    <span className="psp-hero-tag">
                                        <i className="fas fa-map-marker-alt" aria-hidden="true" />
                                        {vacancy.units_and_staffing || 'Conforme edital'}
                                    </span>
                                )}
                                {vacancy.edital_number && (
                                    <span className="psp-hero-tag">
                                        <i className="fas fa-file-alt" aria-hidden="true" />
                                        Edital: {vacancy.edital_number}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Status Badge */}
                        <div className="psp-job-hero-status">
                            {isOpen && (
                                <span className="psp-status-badge psp-status-badge--open psp-status-badge--lg">
                                    ● Inscrições Abertas
                                </span>
                            )}
                            {isUpcoming && (
                                <span className="psp-status-badge psp-status-badge--upcoming psp-status-badge--lg">
                                    ⏳ Em Breve
                                </span>
                            )}
                            {isClosed && (
                                <span className="psp-status-badge psp-status-badge--closed psp-status-badge--lg">
                                    ✕ Inscrições Encerradas
                                </span>
                            )}

                            {endDate && (
                                <div className="psp-job-hero-deadline">
                                    Prazo final: <strong>{endDate.format('DD/MM/YYYY')}</strong>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* BOX DE AÇÃO DE CANDIDATURA */}
                    <div className="psp-apply-action-box">
                        {isSubscribed ? (
                            <div className="psp-subscribed-banner psp-subscribed-banner--full">
                                <div className="psp-subscribed-icon">
                                    <i className="fas fa-check" aria-hidden="true" />
                                </div>
                                <div className="psp-subscribed-banner__content">
                                    <strong className="psp-subscribed-banner__title">
                                        Candidatura Realizada!
                                    </strong>
                                    <span className="psp-subscribed-banner__meta">
                                        Número de Inscrição: <strong>#{subscription ? subscription.subscription_number : ''}</strong>
                                        {subscription && subscription.created_at && (
                                            <> • Realizada em {moment(subscription.created_at).format('DD/MM/YYYY [às] HH:mm')}</>
                                        )}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate(`${base}/home`)}
                                    className="psp-btn psp-btn--outline psp-btn--inline psp-btn--success-outline"
                                >
                                    Ver minhas inscrições
                                </button>
                            </div>
                        ) : isOpen ? (
                            <>
                                <div>
                                    <strong className="psp-apply-action-box__title">
                                        Interessado nesta vaga?
                                    </strong>
                                    <span className="psp-apply-action-box__text">
                                        {isAuthenticated
                                            ? 'Confirme seus dados e envie sua candidatura para participar do processo seletivo.'
                                            : 'Entre na sua Área do Candidato ou crie uma conta gratuita para se inscrever.'}
                                    </span>
                                </div>

                                <button
                                    type="button"
                                    className="psp-btn-apply"
                                    onClick={handleApplyClick}
                                    disabled={applying}
                                >
                                    {applying ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                                            Enviando candidatura...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane" aria-hidden="true" />
                                            {isAuthenticated ? 'Candidatar-se para esta vaga' : 'Entrar para se Candidatar'}
                                        </>
                                    )}
                                </button>
                            </>
                        ) : isUpcoming ? (
                            <div className="psp-apply-action-box__notice psp-apply-action-box__notice--upcoming">
                                <i className="fas fa-clock psp-icon--mr-8" />
                                As inscrições para esta vaga abrirão em {initialDate ? initialDate.format('DD/MM/YYYY') : 'breve'}.
                            </div>
                        ) : (
                            <div className="psp-apply-action-box__notice">
                                <i className="fas fa-lock psp-icon--mr-8" />
                                O período de inscrições para esta vaga está encerrado.
                            </div>
                        )}
                    </div>
                </div>

                {/* SEÇÃO 1: DESCRIÇÃO GERAL */}
                {vacancy.description && (
                    <div className="psp-job-section-card">
                        <h2 className="psp-job-section-title">
                            <i className="fas fa-align-left psp-icon--blue" />
                            Descrição da Vaga
                        </h2>
                        <div className="psp-job-section-text">{vacancy.description}</div>
                    </div>
                )}

                {/* SEÇÃO 2: ATRIBUIÇÕES E RESPONSABILIDADES */}
                {vacancy.responsibilities_and_duties && (
                    <div className="psp-job-section-card">
                        <h2 className="psp-job-section-title">
                            <i className="fas fa-tasks psp-icon--blue" />
                            Responsabilidades e Atribuições
                        </h2>
                        <div className="psp-job-section-text">{vacancy.responsibilities_and_duties}</div>
                    </div>
                )}

                {/* SEÇÃO 3: REQUISITOS E QUALIFICAÇÕES */}
                {(vacancy.requirements_and_qualifications || vacancy.academic_background) && (
                    <div className="psp-job-section-card">
                        <h2 className="psp-job-section-title">
                            <i className="fas fa-user-check psp-icon--blue" />
                            Requisitos e Qualificações
                        </h2>
                        {vacancy.academic_background && (
                            <div className="psp-job-requirement-education">
                                <strong>Escolaridade Mínima Exigida: </strong>
                                <span className="psp-hero-tag">
                                    {vacancy.academic_background}
                                </span>
                            </div>
                        )}
                        {vacancy.requirements_and_qualifications && (
                            <div className="psp-job-section-text">{vacancy.requirements_and_qualifications}</div>
                        )}
                    </div>
                )}

                {/* SEÇÃO 4: COMPETÊNCIAS & DESEJÁVEIS */}
                {(vacancy.behavioral_skills || vacancy.requirements_desirable) && (
                    <div className="psp-job-section-card">
                        <h2 className="psp-job-section-title">
                            <i className="fas fa-star psp-icon--blue" />
                            Competências e Diferenciais
                        </h2>
                        {vacancy.behavioral_skills && (
                            <div className="psp-job-subsection">
                                <strong className="psp-job-subsection__title">
                                    Habilidades Comportamentais:
                                </strong>
                                <div className="psp-job-section-text">{vacancy.behavioral_skills}</div>
                            </div>
                        )}
                        {vacancy.requirements_desirable && (
                            <div>
                                <strong className="psp-job-subsection__title">
                                    Requisitos Desejáveis:
                                </strong>
                                <div className="psp-job-section-text">{vacancy.requirements_desirable}</div>
                            </div>
                        )}
                    </div>
                )}

                {/* SEÇÃO 5: INFORMAÇÕES SOBRE A PROVA */}
                {vacancy.has_exam && (
                    <div className="psp-job-section-card">
                        <h2 className="psp-job-section-title">
                            <i className="fas fa-pencil-alt psp-icon--amber" />
                            Informações sobre a Prova
                        </h2>
                        <div className="psp-exam-alert-card">
                            <div className="psp-exam-grid">
                                {vacancy.exam_type && (
                                    <div>
                                        <span className="psp-exam-label">
                                            Formato da Prova
                                        </span>
                                        <div className="psp-exam-value">
                                            {vacancy.exam_type}
                                        </div>
                                    </div>
                                )}
                                {vacancy.exam_date && (
                                    <div>
                                        <span className="psp-exam-label">
                                            Data da Prova
                                        </span>
                                        <div className="psp-exam-value">
                                            {moment(vacancy.exam_date).format('DD/MM/YYYY')}
                                        </div>
                                    </div>
                                )}
                                {vacancy.exam_local && (
                                    <div>
                                        <span className="psp-exam-label">
                                            Local
                                        </span>
                                        <div className="psp-exam-value">
                                            {vacancy.exam_local}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {vacancy.exam_instructions && (
                                <div className="psp-exam-instructions">
                                    <strong className="psp-exam-instructions__title">
                                        Instruções para realização:
                                    </strong>
                                    <div className="psp-exam-instructions__text">
                                        {vacancy.exam_instructions}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* SEÇÃO 6: ARQUIVOS E EDITAIS ANEXOS */}
                {filesList.length > 0 && (
                    <div className="psp-job-section-card">
                        <h2 className="psp-job-section-title">
                            <i className="fas fa-paperclip psp-icon--blue" />
                            Documentos e Editais Anexos
                        </h2>
                        <div className="psp-item-list">
                            {filesList.map((file, idx) => (
                                <div
                                    key={idx}
                                    className="psp-list-item"
                                >
                                    <div className="psp-list-item__main">
                                        <i className="fas fa-file-pdf psp-list-item__file-icon" />
                                        <span className="psp-list-item__title">
                                            {file.filename || `Anexo ${idx + 1}`}
                                        </span>
                                    </div>
                                    <a
                                        href={file.files3}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="psp-btn psp-btn--outline psp-btn--inline psp-btn--sm"
                                    >
                                        <i className="fas fa-download psp-icon--mr-6" />
                                        Baixar
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* SEÇÃO 7: OUTRAS VAGAS DESTE PROCESSO */}
                {vacancy.other_vacancies && vacancy.other_vacancies.length > 0 && (
                    <div className="psp-job-section-card">
                        <h2 className="psp-job-section-title">
                            <i className="fas fa-list-ul psp-icon--blue" />
                            Outras vagas deste mesmo processo seletivo
                        </h2>
                        <div className="psp-related-grid">
                            {vacancy.other_vacancies.map((ov) => (
                                <button
                                    type="button"
                                    key={ov.id}
                                    className="psp-related-card"
                                    onClick={() => navigate(`${base}/vagas/${ov.id}`)}
                                >
                                    <strong className="psp-related-card__title">
                                        {ov.title}
                                    </strong>
                                    <span className="psp-related-card__meta">
                                        {ov.area_role_name || ov.role_name || 'Geral'}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL DE CONFIRMAÇÃO DE CANDIDATURA */}
            {showConfirmModal && (
                <div className="psp-modal-backdrop" onClick={() => setShowConfirmModal(false)}>
                    <div className="psp-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="psp-modal__header">
                            <h2>Confirmar Candidatura</h2>
                        </div>
                        <div className="psp-modal__body">
                            <p className="psp-confirm__intro">
                                Você está se candidatando para a vaga:
                            </p>
                            <div className="psp-confirm__vacancy">
                                <strong className="psp-confirm__vacancy-title">
                                    {vacancy.title}
                                </strong>
                                <span className="psp-confirm__vacancy-meta">
                                    Edital: {vacancy.edital_number || 'S/N'}
                                </span>
                            </div>

                            {candidate && (
                                <div className="psp-confirm__candidate">
                                    <p>
                                        <strong>Candidato:</strong> {candidate.name}
                                    </p>
                                    <p>
                                        <strong>E-mail:</strong> {candidate.email}
                                    </p>
                                    <p>
                                        <strong>Sua Escolaridade:</strong> {candidate.schooling || 'Não informada'}
                                    </p>
                                    {vacancy.academic_background && (
                                        <p>
                                            <strong>Escolaridade Mínima Exigida:</strong> {vacancy.academic_background}
                                        </p>
                                    )}
                                </div>
                            )}

                            {!schoolingComparison.meets && (
                                <div className="psp-schooling-warning">
                                    <div className="psp-schooling-warning__body">
                                        <i
                                            className="fas fa-exclamation-triangle psp-schooling-warning__icon"
                                            aria-hidden="true"
                                        />
                                        <div>
                                            <strong className="psp-schooling-warning__title">
                                                Escolaridade incompatível:
                                            </strong>
                                            <span className="psp-schooling-warning__text">
                                                Esta vaga exige no mínimo <strong>{schoolingComparison.required}</strong>, mas sua escolaridade cadastrada é <strong>{schoolingComparison.current}</strong>.
                                            </span>
                                        </div>
                                    </div>
                                    <div className="psp-schooling-warning__footer">
                                        <button
                                            type="button"
                                            className="psp-link psp-link--dark"
                                            onClick={() => {
                                                setShowConfirmModal(false);
                                                navigate(`${base}/perfil`);
                                            }}
                                        >
                                            Atualizar meu cadastro na Área do Candidato &rarr;
                                        </button>
                                    </div>
                                </div>
                            )}

                            <p className="psp-confirm__note">
                                Ao confirmar, sua candidatura será registrada e você poderá acompanhar o status pelo seu painel de candidato.
                            </p>
                        </div>
                        <div className="psp-modal__footer">
                            <button
                                type="button"
                                className="psp-btn psp-btn--outline"
                                onClick={() => setShowConfirmModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className={`psp-btn psp-btn--primary psp-btn--confirm`}
                                onClick={handleConfirmApply}
                                disabled={applying}
                                title={'Confirmar Inscrição'}
                            >
                                {applying ? 'Inscrevendo...' : 'Confirmar Inscrição'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobDetail;
